import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Alert, AlertDescription } from "../ui/alert";
import { Spinner } from "../ui/spinner";
import { Badge } from "../ui/badge";
import { AlertTriangle, Shield } from "lucide-react";
import { ControlService } from "../../services/controlService";

const ApplyControlToRiskFromRiskView = ({
  open,
  onClose,
  risk,
  organizationId,
  businessUnitId,
  onControlApplied,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [availableControls, setAvailableControls] = useState([]);
  const [selectedControlId, setSelectedControlId] = useState("");
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open && organizationId && risk?.id) {
      loadAvailableControls();
    } else if (open && !organizationId) {
      setError("Missing required organization information");
      setLoading(false);
    }
  }, [open, organizationId, businessUnitId, risk?.id]);

  const loadAvailableControls = async () => {
    try {
      setLoading(true);
      setError("");

      // getControls handles both organization and business unit level controls
      const controlsData = await ControlService.getControls(
        organizationId,
        businessUnitId, // This can be undefined for org-level controls
        getAccessTokenSilently
      );

      // Filter out controls that are already applied to the risk
      const appliedControlIds = new Set(risk?.Controls?.map((c) => c.id) || []);
      const filteredControls = controlsData.filter(
        (control) => !appliedControlIds.has(control.id)
      );
      setAvailableControls(filteredControls);
    } catch (err) {
      setError(err.message);
      console.error("Error loading available controls:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyControl = async () => {
    if (!selectedControlId) {
      setError("Please select a control to apply");
      return;
    }

    try {
      setApplying(true);
      setError("");

      await ControlService.applyControlToRisk(
        selectedControlId,
        risk.id,
        getAccessTokenSilently
      );

      // Notify parent component to refresh data
      if (onControlApplied) {
        onControlApplied(risk.id, selectedControlId);
      }

      // Reset and close
      setSelectedControlId("");
      onClose();
    } catch (err) {
      setError(err.message);
      console.error("Error applying control:", err);
    } finally {
      setApplying(false);
    }
  };

  const handleClose = () => {
    if (!applying) {
      setSelectedControlId("");
      setError("");
      onClose();
    }
  };

  const getRiskLevelColor = (riskLevel) => {
    if (riskLevel >= 15) return "bg-red-100 text-red-800";
    if (riskLevel >= 8) return "bg-orange-100 text-orange-800";
    return "bg-green-100 text-green-800";
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Apply Control to Risk</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {error && (
            <Alert className="border-red-200 bg-red-50">
              <AlertDescription className="text-red-800">
                {error}
              </AlertDescription>
            </Alert>
          )}

          {/* Control Selection */}
          {loading ? (
            <div className="flex justify-center p-4">
              <Spinner />
            </div>
          ) : (
            <Select
              value={selectedControlId}
              onValueChange={setSelectedControlId}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a control to apply" />
              </SelectTrigger>
              <SelectContent>
                {availableControls.map((control) => (
                  <SelectItem key={control.id} value={control.id}>
                    {control.name || control.description}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleClose} disabled={applying}>
              Cancel
            </Button>
            <Button
              onClick={handleApplyControl}
              disabled={applying || !selectedControlId}
            >
              {applying ? <Spinner className="mr-2" /> : null}
              Apply Control
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ApplyControlToRiskFromRiskView;
